import { forwardRef, type ComponentPropsWithRef } from "react";

import { cva, type VariantProps } from "class-variance-authority";

import type { colors } from "@eisox/tokens";

import { cn } from "../index";

/* -------------------------------------------------------------------------------------------------
 * Root
 * -----------------------------------------------------------------------------------------------*/

type DataListOrientation = "horizontal" | "vertical";
type DataListTrim = "normal" | "start" | "end" | "both";

const dataListRootVariants = cva("group/data-list font-normal text-start [overflow-wrap:anywhere]", {
  variants: {
    orientation: {
      vertical: "flex flex-col gap-[20px]",
      horizontal: "grid [grid-template-columns:auto_minmax(0,_1fr)] gap-x-[8px] gap-y-[20px]",
    },
  },
  defaultVariants: {
    orientation: "horizontal",
  },
});

interface DataListProps extends ComponentPropsWithRef<"dl">, VariantProps<typeof dataListRootVariants> {
  trim?: DataListTrim;
}

const Root = forwardRef<HTMLDListElement, DataListProps>((props, ref) => {
  const { orientation: orientationProp, trim = "normal", className, children, ...rootProps } = props;
  const orientation: DataListOrientation = orientationProp ?? "horizontal";

  return (
    <dl
      {...rootProps}
      ref={ref}
      data-orientation={orientation}
      data-trim={trim}
      className={cn(dataListRootVariants({ orientation, className }))}
    >
      {children}
    </dl>
  );
});

Root.displayName = "DataList";

/* -------------------------------------------------------------------------------------------------
 * Item
 * -----------------------------------------------------------------------------------------------*/

const dataListItemVariants = cva("contents", {
  variants: {
    align: {
      baseline: "items-baseline",
      start: "items-start",
      center: "items-center",
      end: "items-end",
      stretch: "items-stretch",
    },
  },
  defaultVariants: {
    align: "baseline",
  },
});

interface ItemProps extends ComponentPropsWithRef<"div">, VariantProps<typeof dataListItemVariants> {}

const Item = forwardRef<HTMLDivElement, ItemProps>((props, ref) => {
  const { align, className, children, ...itemProps } = props;

  return (
    <div
      {...itemProps}
      ref={ref}
      className={cn(
        `contents group-data-[trim=both]/data-list:first:mt-[-4.5px] first:group-data-[trim=start]/data-list:mt-[-4.5px] group-data-[trim=both]/data-list:last:mb-[-5.5px] last:group-data-[trim=end]/data-list:mb-[-5.5px]`,
        className,
      )}
    >
      <div
        className={cn(
          `contents group-data-[orientation=horizontal]/data-list:col-span-2 group-data-[orientation=horizontal]/data-list:grid group-data-[orientation=horizontal]/data-list:[grid-template-columns:subgrid] group-data-[orientation=vertical]/data-list:flex group-data-[orientation=vertical]/data-list:flex-col`,
          dataListItemVariants({ align }),
        )}
      >
        {children}
      </div>
    </div>
  );
});

Item.displayName = "Item";

/* -------------------------------------------------------------------------------------------------
 * Label
 * -----------------------------------------------------------------------------------------------*/

interface LabelProps extends Omit<ComponentPropsWithRef<"dt">, "color"> {
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  color?: keyof typeof colors.primary;
}

const Label = forwardRef<HTMLDivElement, LabelProps>((props, ref) => {
  const { className, ...labelProps } = props;
  return (
    <dt
      {...labelProps}
      ref={ref}
      className={cn(
        `group-data-[orientation=vertical]/data-list:min-w-0 flex text-base text-primary-dark-gray group-data-[orientation=horizontal]/data-list:min-w-[120px]`,
        className,
      )}
    />
  );
});

Label.displayName = "Label";

/* -------------------------------------------------------------------------------------------------
 * Value
 * -----------------------------------------------------------------------------------------------*/

interface ValueProps extends Omit<ComponentPropsWithRef<"dd">, "color"> {
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  color?: keyof typeof colors.primary;
}

const Value = forwardRef<HTMLDivElement, ValueProps>((props, ref) => {
  const { className, ...valueProps } = props;
  return (
    <dd
      {...valueProps}
      ref={ref}
      className={cn(
        `m-0 min-w-0 group-data-[orientation=vertical]/data-list:mt-0 group-data-[orientation=vertical]/data-list:mb-0 first:group-data-[orientation=horizontal]/data-list:mt-0 last:group-data-[orientation=horizontal]/data-list:mb-0 flex text-base group-data-[orientation=horizontal]/data-list:mt-[-0.25em] group-data-[orientation=horizontal]/data-list:mb-[-0.25em]`,
        className,
      )}
    />
  );
});

Value.displayName = "Value";

export { Item, Label, Root, Value };
export type { DataListProps, ItemProps, LabelProps, ValueProps };
